import React, {useEffect, useRef, useState} from 'react';
import {useLazyGetReportEmbeddedUrlQuery} from 'src/redux/queries/report';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

import type {
  ContentOptions,
  DashboardExperience,
  EmbeddingContext,
  FrameOptions,
} from 'amazon-quicksight-embedding-sdk';
import {createEmbeddingContext} from 'amazon-quicksight-embedding-sdk';

export interface ReportViewerProps {
  dashboardId: string;
}

// Module-level cache for embedding context (survives component re-renders)
let cachedEmbeddingContext: EmbeddingContext | null = null;

const getEmbeddingContext = async (): Promise<EmbeddingContext> => {
  if (!cachedEmbeddingContext) {
    cachedEmbeddingContext = await createEmbeddingContext();
  }
  return cachedEmbeddingContext;
};

export const ReportViewer = React.memo<ReportViewerProps>(({dashboardId}) => {
  const dashboardRef = useRef<HTMLDivElement>(null);
  const embeddedDashboardRef = useRef<DashboardExperience | null>(null);
  const isEmbeddingRef = useRef(false);
  const [dashboardUrl, setDashboardUrl] = useState<string>();

  const [getReportEmbeddedUrl] = useLazyGetReportEmbeddedUrlQuery();
  const {setAlert} = useAlert();

  useEffect(() => {
    if (!dashboardId) return;

    void getReportEmbeddedUrl(dashboardId).then(result => {
      if (result.data) {
        setDashboardUrl(result.data);
      }
    });
  }, [dashboardId]);

  useEffect(() => {
    if (!dashboardUrl || !dashboardRef.current || isEmbeddingRef.current) {
      return;
    }

    const embed = async () => {
      if (!dashboardRef.current) return;

      isEmbeddingRef.current = true;

      try {
        const context = await getEmbeddingContext();

        dashboardRef.current.innerHTML = '';

        const frameOptions: FrameOptions = {
          url: dashboardUrl,
          container: dashboardRef.current,
          height: '100%',
          width: '100%',
          resizeHeightOnSizeChangedEvent: true,
        };

        const contentOptions: ContentOptions = {
          toolbarOptions: {
            export: true,
            undoRedo: true,
            reset: true,
          },
        };

        const newEmbeddedDashboard = await context.embedDashboard(
          frameOptions,
          contentOptions
        );
        embeddedDashboardRef.current = newEmbeddedDashboard;
      } finally {
        isEmbeddingRef.current = false;
      }
    };

    embed().catch((error: unknown) =>
      setAlert(parseErrorMessage(error), 'error')
    );
  }, [dashboardUrl]);

  useEffect(() => {
    if (dashboardId && embeddedDashboardRef.current) {
      embeddedDashboardRef.current
        .navigateToDashboard(dashboardId, {})
        .catch((error: unknown) => setAlert(parseErrorMessage(error), 'error'));
    }
  }, [dashboardId]);

  return (
    <div
      ref={dashboardRef}
      style={{height: 'calc(100vh - 150px)', minHeight: '400px'}}
    />
  );
});
